/**
 * Frontend Field-to-Field Conditional Logic.
 *
 * Registers a `gform_rule_pre_evaluation` filter that detects merge tag
 * patterns ({:FIELD_ID:value}) in rule values and resolves them to the
 * referenced field's current value using GF's merge tag system.
 *
 * All merge tags in the value are resolved (not just the first). When the
 * value contains exactly one merge tag, `resolvedFieldId` is set on the
 * rule so downstream consumers (e.g. date-logic.ts) can look up the
 * target field's format.
 *
 * Note: CL dependency registration is handled by the PHP class via
 * GFFormDisplay::add_init_script(), which outputs a GPACLFieldToField
 * instance that registers dependencies in gf_form_conditional_logic.
 *
 * @package
 */

interface FieldToFieldRule {
	fieldId: string | number;
	operator: string;
	value: string;
	/** Field IDs of all resolved merge tags, in order of appearance. */
	resolvedFieldIds?: string[];
}

/**
 * Initialize the field-to-field frontend module.
 */
export function initFieldToField(): void {
	if (typeof gform === 'undefined' || !gform.addFilter) {
		return;
	}

	gform.addFilter(
		'gform_rule_pre_evaluation',
		(rule: FieldToFieldRule, formId: number): FieldToFieldRule => {
			// Check if the rule value contains a merge tag pattern.
			// Use '{' (not '{:') so we also match {Label:ID} format merge tags
			// like {Time A:1}. GFMergeTag.parseMergeTags() regex only matches
			// patterns with :DIGIT, so {today}, {monday}, etc. won't match.
			if (
				typeof GFMergeTag === 'undefined' ||
				!rule.value ||
				rule.value.indexOf('{') === -1
			) {
				return rule;
			}

			// A failure here must never propagate: GF evaluates rules inside
			// gf_apply_rules(), so a thrown error aborts conditional logic for
			// the whole form and leaves every field stuck in its rendered state.
			try {
				const mergeTags = GFMergeTag.parseMergeTags(rule.value);
				if (!mergeTags || !mergeTags.length) {
					return rule;
				}

				// Resolve all merge tags in the value string.
				// Each match: [0] = full tag, [1] = field ID, [3] = modifier
				let resolved = rule.value;
				for (const tag of mergeTags) {
					const tagValue = GFMergeTag.getMergeTagValue(
						formId,
						tag[1],
						tag[3]
					);
					// getMergeTagValue() returns false when the referenced input
					// is missing from the DOM. Fall back to an empty string so we
					// compare against a value rather than the literal merge tag.
					resolved = resolved.replace(
						tag[0],
						typeof tagValue === 'string' ? tagValue : ''
					);
				}
				rule.value = resolved;

				// Stash all resolved field IDs so downstream consumers can look
				// up target field properties (e.g. date format for parsing).
				rule.resolvedFieldIds = mergeTags.map((tag) => tag[1]);
			} catch (e) {
				// eslint-disable-next-line no-console
				console.error(
					'[GPACL] Failed to resolve field-to-field merge tags in rule value:',
					rule.value,
					e
				);
				return rule;
			}

			// Apply number formatting if the source field has a number format.
			if (
				// eslint-disable-next-line camelcase
				typeof gf_get_field_number_format === 'function' &&
				// eslint-disable-next-line camelcase
				typeof gf_format_number === 'function'
			) {
				// eslint-disable-next-line camelcase
				const numberFormat = gf_get_field_number_format(
					rule.fieldId,
					formId,
					'value'
				);
				if (numberFormat) {
					// eslint-disable-next-line camelcase
					rule.value = gf_format_number(rule.value, numberFormat);
				}
			}

			return rule;
		}
	);
}

// Auto-initialize when this module is imported.
initFieldToField();
